import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { ArrowLeft, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Order {
  id: string;
  orderNumber: string;
  sellerId: string;
  sellerName?: string;
  total: number;
  createdAt: any;
  status: string;
}

export default function PrivateSaleOrders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) {
      navigate('/dashboard/login');
      return;
    }
    loadOrders();
  }, [currentUser]);

  const loadOrders = async () => {
    try {
      const ordersRef = collection(db, 'orders');
      const ordersQuery = query(
        ordersRef,
        where('buyerId', '==', currentUser.uid),
        where('isPrivateSale', '==', true),
        orderBy('createdAt', 'desc')
      );

      const ordersSnapshot = await getDocs(ordersQuery);
      const ordersData = await Promise.all(ordersSnapshot.docs.map(async orderDoc => {
        const data = orderDoc.data();
        
        // Buscar nome do vendedor
        let sellerName = '';
        try {
          // Buscar dados do vendedor
          const sellerRef = doc(db, 'users', data.sellerId);
          const sellerDoc = await getDoc(sellerRef);
          if (sellerDoc.exists()) {
            // Buscar dados da empresa do vendedor
            const companyRef = doc(db, 'companies', sellerDoc.data().companyId);
            const companyDoc = await getDoc(companyRef);
            if (companyDoc.exists()) {
              const companyData = companyDoc.data();
              sellerName = companyData.nomeComercial || companyData.nomeEmpresa || companyData.commercialName || companyData.legalName;
            }
          }
        } catch (err) {
          console.error('Erro ao buscar nome do vendedor:', err);
        }
        
        return {
          id: orderDoc.id,
          orderNumber: data.orderNumber || orderDoc.id,
          sellerId: data.sellerId,
          sellerName: sellerName || data.sellerName || 'Empresa não encontrada',
          total: data.total,
          createdAt: data.createdAt,
          status: data.status
        };
      }));

      setOrders(ordersData);
      setError('');
    } catch (err) {
      console.error('Erro ao carregar pedidos:', err);
      setError('Erro ao carregar pedidos. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order =>
    order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.sellerName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate('/dashboard/drop')}
              className="inline-flex items-center gap-2 text-primary hover:text-primary-dark"
            >
              <ArrowLeft size={20} />
              Voltar para Area Drop
            </button>
            <h2 className="text-2xl font-bold text-secondary">Meus Pedidos</h2>
          </div>

          <div className="relative mb-6">
            <input
              type="text"
              placeholder="Pesquisar por número do pedido ou vendedor..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          </div>

          {error && (
            <div className="bg-red-50 text-red-500 p-4 rounded-md mb-6">
              {error}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nº do Pedido
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vendedor
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor Total
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {order.orderNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.sellerName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {order.total.toLocaleString('pt-PT', {
                        style: 'currency',
                        currency: 'EUR'
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        order.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                        order.status === 'completed' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {order.status === 'pending' ? 'Pendente' :
                         order.status === 'processing' ? 'Em Processamento' :
                         order.status === 'completed' ? 'Concluído' :
                         'Desconhecido'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => navigate(`/dashboard/drop/orders/${order.id}`)}
                        className="text-primary hover:text-primary-dark"
                      >
                        Ver Detalhes
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredOrders.length === 0 && (
              <div className="text-center py-12">
                <p className="text-sm text-gray-500">
                  {searchTerm
                    ? 'Nenhum pedido encontrado para a sua pesquisa.'
                    : 'Você ainda não tem pedidos.'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
